import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ProductCard } from "./ProductCard"
import { AnimatedCard } from "./AnimatedCard"
import { GlassCard } from "./GlassCard"
import { useApexState } from "@/hooks/useApexState"
import { 
  Heart, 
  Plus, 
  Share2, 
  Crown,
  X,
  Search,
  Filter,
  Grid3X3,
  List,
  ShoppingBag
} from "lucide-react"

interface WishlistItem {
  id: string
  productId: string
  name: string
  price: number
  image: string
  isApexLocked?: boolean
  isLimited?: boolean
  addedDate: string
  collection?: string
  priority: 'low' | 'medium' | 'high'
}

interface WishlistCollection {
  id: string
  name: string
  description: string
  itemCount: number
  isPrivate: boolean
  createdDate: string
}

interface WishlistManagerProps {
  isOpen: boolean
  onClose: () => void
}

const mockWishlistItems: WishlistItem[] = [
  {
    id: "1",
    productId: "apex-joggers",
    name: "Apex Kinetic Joggers",
    price: 180,
    image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=300&h=400&fit=crop",
    isApexLocked: true,
    addedDate: "2 days ago",
    collection: "Movement Essentials",
    priority: "high"
  },
  {
    id: "2",
    productId: "architect-hoodie",
    name: "Architect Hoodie",
    price: 220,
    image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=400&fit=crop",
    isApexLocked: true,
    isLimited: true,
    addedDate: "1 week ago",
    collection: "Architect Collection",
    priority: "high"
  },
  {
    id: "3",
    productId: "form-tank",
    name: "Form Training Tank",
    price: 85,
    image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=300&h=400&fit=crop",
    addedDate: "3 days ago",
    collection: "Training Gear",
    priority: "medium"
  },
  {
    id: "4",
    productId: "urban-jacket",
    name: "Urban Explorer Jacket",
    price: 280,
    image: "https://images.unsplash.com/photo-1551028719-00167b16eac5?w=300&h=400&fit=crop",
    isApexLocked: true,
    addedDate: "5 days ago",
    priority: "low"
  }
]

const mockCollections: WishlistCollection[] = [
  {
    id: "1",
    name: "Movement Essentials",
    description: "Core pieces for active lifestyle",
    itemCount: 1,
    isPrivate: false,
    createdDate: "2 weeks ago"
  },
  {
    id: "2",
    name: "Architect Collection",
    description: "Luxury pieces for elevated style",
    itemCount: 1,
    isPrivate: false,
    createdDate: "1 week ago"
  },
  {
    id: "3",
    name: "Training Gear",
    description: "Performance-focused items",
    itemCount: 1,
    isPrivate: true,
    createdDate: "3 days ago"
  }
]

export function WishlistManager({ isOpen, onClose }: WishlistManagerProps) {
  const { state, actions } = useApexState()
  const [activeView, setActiveView] = useState<'items' | 'collections'>('items')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCollection, setSelectedCollection] = useState("all")
  const [showCreateCollection, setShowCreateCollection] = useState(false)
  const [newCollectionName, setNewCollectionName] = useState("")

  const filteredItems = mockWishlistItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCollection = selectedCollection === "all" || item.collection === selectedCollection
    return matchesSearch && matchesCollection
  })

  const getPriorityColor = (priority: WishlistItem['priority']) => {
    switch (priority) {
      case 'high': return 'text-primary'
      case 'medium': return 'text-accent' 
      case 'low': return 'text-muted-foreground'
    }
  }

  const handleCreateCollection = () => {
    if (newCollectionName.trim()) {
      // Add collection logic here
      console.log('Creating collection:', newCollectionName)
      setNewCollectionName("")
      setShowCreateCollection(false)
    }
  }

  const handleShareWishlist = () => {
    // Share functionality
    console.log('Sharing wishlist')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="absolute top-0 right-0 w-full max-w-2xl h-full">
        <GlassCard className="h-full rounded-none rounded-l-lg">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Heart size={20} className="text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Wishlist</h2>
                <Badge className="bg-primary/20 text-primary text-xs">
                  {mockWishlistItems.length}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleShareWishlist}>
                  <Share2 size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X size={20} />
                </Button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-1">
                <Button
                  variant={activeView === 'items' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('items')}
                  className="text-xs"
                >
                  Items ({mockWishlistItems.length})
                </Button>
                <Button
                  variant={activeView === 'collections' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('collections')}
                  className="text-xs"
                >
                  Collections ({mockCollections.length})
                </Button>
              </div>
              
              {activeView === 'items' && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  >
                    {viewMode === 'grid' ? <List size={16} /> : <Grid3X3 size={16} />}
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Filter size={16} />
                  </Button>
                </div>
              )}
            </div>

            {/* Search and Filters */}
            {activeView === 'items' && (
              <div className="p-4 space-y-3 border-b border-white/10">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search wishlist..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-input/50 border-white/20"
                  />
                </div>
                
                <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                  <Button
                    variant={selectedCollection === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCollection("all")}
                    className="whitespace-nowrap"
                  >
                    All Items
                  </Button>
                  {mockCollections.map(collection => (
                    <Button
                      key={collection.id}
                      variant={selectedCollection === collection.name ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCollection(collection.name)}
                      className="whitespace-nowrap"
                    >
                      {collection.name} ({collection.itemCount})
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeView === 'items' ? (
                <div className="space-y-4">
                  {filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                      <Heart size={48} className="text-muted-foreground opacity-50 mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No items found
                      </h3>
                      <p className="text-muted-foreground">
                        {searchQuery ? "Try a different search term" : "Start adding items to your wishlist"}
                      </p>
                    </div>
                  ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 gap-4">
                      {filteredItems.map(item => (
                        <AnimatedCard key={item.id} hoverScale>
                          <div className="relative">
                            <ProductCard
                              id={item.id}
                              name={item.name}
                              price={item.price}
                              image={item.image}
                              isApexLocked={item.isApexLocked}
                              isLimited={item.isLimited}
                              isWishlisted={true}
                              onWishlist={() => actions.removeFromWishlist(item.id)}
                              onAddToCart={() => actions.addToCart(item)}
                            />
                            <div className="absolute top-2 left-2">
                              <div className={`w-2 h-2 rounded-full ${getPriorityColor(item.priority) === 'text-primary' ? 'bg-primary' : getPriorityColor(item.priority) === 'text-accent' ? 'bg-accent' : 'bg-muted'}`} />
                            </div>
                          </div>
                        </AnimatedCard>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredItems.map(item => (
                        <AnimatedCard key={item.id}>
                          <Card className="bg-card/50 border-white/10">
                            <CardContent className="p-4">
                              <div className="flex gap-4">
                                <div className="w-16 h-16 rounded-lg overflow-hidden">
                                  <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <h4 className="font-medium text-foreground leading-tight">
                                        {item.name}
                                      </h4>
                                      <p className="text-lg font-semibold text-primary">
                                        ${item.price}
                                      </p>
                                      <div className="flex items-center gap-2 mt-2">
                                        {item.isApexLocked && (
                                          <Badge className="bg-primary/20 text-primary text-xs">
                                            <Crown size={8} className="mr-1" />
                                            Apex Only
                                          </Badge>
                                        )}
                                        {item.isLimited && (
                                          <Badge variant="destructive" className="text-xs">
                                            Limited
                                          </Badge>
                                        )}
                                        <span className={`text-xs ${getPriorityColor(item.priority)}`}>
                                          {item.priority} priority
                                        </span>
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                      <Button variant="outline" size="sm">
                                        <ShoppingBag size={14} />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => actions.removeFromWishlist(item.id)}
                                      >
                                        <Heart size={14} className="fill-current text-primary" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </AnimatedCard>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Collections</h3>
                    <Button
                      size="sm"
                      onClick={() => setShowCreateCollection(true)}
                      className="bg-primary hover:bg-primary/90 text-black"
                    >
                      <Plus size={16} className="mr-1" />
                      New Collection
                    </Button>
                  </div>

                  {/* Create Collection Form */}
                  {showCreateCollection && (
                    <AnimatedCard>
                      <Card className="bg-card/50 border-white/10">
                        <CardContent className="p-4 space-y-3">
                          <Input
                            placeholder="Collection name"
                            value={newCollectionName}
                            onChange={(e) => setNewCollectionName(e.target.value)}
                            className="bg-input/50 border-white/20"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowCreateCollection(false)}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleCreateCollection}
                              disabled={!newCollectionName.trim()}
                              className="flex-1 bg-primary hover:bg-primary/90 text-black"
                            >
                              Create
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </AnimatedCard>
                  )}

                  {/* Collections List */}
                  <div className="space-y-3">
                    {mockCollections.map(collection => (
                      <AnimatedCard key={collection.id}>
                        <Card className="bg-card/50 border-white/10">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-foreground">
                                    {collection.name}
                                  </h4>
                                  {collection.isPrivate && (
                                    <Badge variant="secondary" className="text-xs">
                                      Private
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {collection.description}
                                </p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                  <span>{collection.itemCount} items</span>
                                  <span>Created {collection.createdDate}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm">
                                  <Share2 size={14} />
                                </Button>
                                <Button variant="outline" size="sm">
                                  View
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </AnimatedCard>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  )
}